import { useEffect } from 'react';
import { FormProvider, useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@mui/material';
import { IconButton } from '@sdb/planning-ui';
import { FilterPopper } from 'components/flex-portal/FlexMatch/components/FilterPopper';
import { PulsingBadge } from 'components/shared/Badge/PulsingBadge';
import { StyledRelativeBox } from 'components/shared/Box/Box.styles';
import { useIsVerticalToolbar } from 'components/shared/ToolbarSet/ToolbarPosition.context';

import AppointmentsOverviewFilterSummary from '../AppointmentsOverviewFilterSummary';
import AppointmentsOverviewFilterFields from './components/AppointmentsOverviewFilterFields';
import { FiltersAnchor } from './AppointmentsOverviewFilters.styles';
import {
  countActiveFilters,
  EMPTY_FILTERS
} from './AppointmentsOverviewFilters.utils';

import type {
  AppointmentsOverviewFiltersForm,
  AppointmentsOverviewFiltersProps
} from './AppointmentsOverviewFilters.types';

/** The anchor's DOM id, which the popper reads back to anchor itself under the icon. */
const APPOINTMENTS_OVERVIEW_FILTERS_ID = 'appointments-overview-filters';

/**
 * The overview's filters behind the toolbar's sliders icon and the summary beside it, as on the takenlijst
 * (`BacklogFilters`): the
 * shared {@link FilterPopper} with pin, drag, Filters wissen and Toepassen, laid over the grid so opening
 * and closing it moves no rows.
 *
 * Picks change a draft; Toepassen commits it, so a planner setting three filters pays for one fetch rather
 * than three. A click outside closes the panel only while the draft has nothing unapplied, so a pick is
 * never dropped without the planner asking for it; Esc and ✕ close it and drop the draft.
 */
const AppointmentsOverviewFilters = ({
  selections,
  activityIds,
  onApply,
  isOpen,
  onOpenChange
}: AppointmentsOverviewFiltersProps) => {
  'use memo';

  // Popper computes placement at runtime, so it cannot come from CSS — but the layout is the
  // toolbar's, so it is read from the toolbar rather than handed over as a prop.
  const isVerticalToolbar = useIsVerticalToolbar();

  const { t } = useTranslation('common', {
    keyPrefix: 'appointmentsOverview.filters'
  });

  const filterMethods = useForm<AppointmentsOverviewFiltersForm>({
    defaultValues: { selections, activityIds }
  });
  // Subscribed through useFormState rather than read off formState in the handler: the proxy only keeps
  // isDirty up to date for a subscriber, and the compiler would memoise a read made during render.
  const { isDirty } = useFormState({ control: filterMethods.control });

  // What is applied can change while the panel is shut — the calendar's hand-over, Filters wissen. Re-seeding
  // the draft then keeps the two from drifting, and drops what Esc or ✕ left behind; while the panel is open
  // the planner's picks are left alone.
  useEffect(() => {
    if (isOpen) return;
    filterMethods.reset({ selections, activityIds });
  }, [isOpen, selections, activityIds, filterMethods]);

  const handleClose = () => onOpenChange(false);

  const handleApplyFilters = filterMethods.handleSubmit((values) => {
    onApply(values);
    handleClose();
  });

  const handleClearFilters = () => {
    filterMethods.reset(EMPTY_FILTERS);
    onApply(EMPTY_FILTERS);
    handleClose();
  };

  // Unapplied picks keep the panel open: a click outside is too easy to make by accident to throw them away.
  const handleClickAway = () => {
    if (!isDirty) handleClose();
  };

  // Counted over what is applied rather than the draft, so the badge reports the grid and not the panel.
  const activeFilterCount = countActiveFilters({ selections, activityIds });

  // aria-label replaces the accessible name, so the count has to be folded into it or a screen reader
  // announces the button without it.
  const accessibleLabel =
    activeFilterCount > 0
      ? t('triggerLabelWithCount', { count: activeFilterCount })
      : t('title');

  return (
    <FiltersAnchor id={APPOINTMENTS_OVERVIEW_FILTERS_ID}>
      <Tooltip
        title={t('title')}
        placement={isVerticalToolbar ? 'left' : 'bottom'}
      >
        <StyledRelativeBox>
          <IconButton
            type="sliders"
            size="small"
            onClick={() => onOpenChange(!isOpen)}
            aria-label={accessibleLabel}
            aria-expanded={isOpen}
          />
          {activeFilterCount > 0 && (
            <PulsingBadge color="success" content={activeFilterCount} />
          )}
        </StyledRelativeBox>
      </Tooltip>
      <AppointmentsOverviewFilterSummary
        selections={selections}
        activityIds={activityIds}
        onOpen={() => onOpenChange(true)}
      />
      <FormProvider {...filterMethods}>
        <FilterPopper
          isOpen={isOpen}
          filterId={APPOINTMENTS_OVERVIEW_FILTERS_ID}
          getAnchorEl={() =>
            document.getElementById(APPOINTMENTS_OVERVIEW_FILTERS_ID)
          }
          filterMainLabel={t('title')}
          // Across the top the panel drops down under the icon; docked to the side it opens into the page
          // to its left.
          placement={isVerticalToolbar ? 'left' : 'bottom-start'}
          // Both filters are on show; there is no second tier to unfold.
          hasMoreFilters={false}
          handleClose={handleClose}
          handleClearFilters={handleClearFilters}
          handleApplyFilters={handleApplyFilters}
          onClickAway={handleClickAway}
        >
          <AppointmentsOverviewFilterFields />
        </FilterPopper>
      </FormProvider>
    </FiltersAnchor>
  );
};

export default AppointmentsOverviewFilters;
